(() => {
  if (customElements.get('aven-product-video-carousel')) return;

  customElements.define('aven-product-video-carousel', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.track = this.querySelector('.aven-video-carousel__track');
      this.items = [...(this.track?.children || [])];
      this.previous = this.querySelector('[data-video-previous]');
      this.next = this.querySelector('[data-video-next]');
      this.navigation = this.querySelector('.aven-video-carousel__navigation');
      this.position = this.querySelector('[data-video-position]');
      if (!this.track || !this.items.length || !this.previous || !this.next || !this.navigation || !this.position) return;

      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') this.goTo(0);
        else if (event.key === 'End') this.goTo(this.items.length - 1);
        else this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });

      if (window.ResizeObserver) {
        this.resize = new ResizeObserver(() => this.scheduleUpdate());
        this.resize.observe(this.track);
      } else window.addEventListener('resize', () => this.scheduleUpdate(), { signal });
      this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.isConnected) this.update();
      });
    }

    geometry() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const left = Math.max(0, Math.min(this.track.scrollLeft, max));
      const origin = this.items[0]?.offsetLeft || 0;
      const starts = this.items.map(item => item.offsetLeft - origin);
      return { max, left, starts };
    }

    update() {
      const { max, left, starts } = this.geometry();
      this.navigation.hidden = this.items.length < 2 || max <= 2;
      this.previous.disabled = left <= 2;
      this.next.disabled = left >= max - 2;
      // Count fully visible tiles; partial tiles while swiping are not new pages.
      let first = starts.findIndex((start, index) => start + this.items[index].offsetWidth > left + 2);
      if (first < 0) first = 0;
      let last = first;
      for (let index = first; index < starts.length; index++) {
        if (starts[index] + this.items[index].offsetWidth <= left + this.track.clientWidth + 2) last = index;
      }
      const label = first === last ? `${first + 1}` : `${first + 1}–${last + 1}`;
      if (this.position.textContent !== label) this.position.textContent = label;
    }

    move(direction) {
      const { left, max, starts } = this.geometry();
      const stops = [...new Set(starts.map(start => Math.min(start, max)))];
      const target = direction > 0
        ? stops.find(stop => stop > left + 2) ?? max
        : stops.slice().reverse().find(stop => stop < left - 2) ?? 0;
      this.scrollToPosition(target);
    }

    goTo(index) {
      const { max, starts } = this.geometry();
      this.scrollToPosition(Math.min(starts[Math.max(0, Math.min(index, starts.length - 1))], max));
    }

    scrollToPosition(left) {
      this.track.scrollTo({ left, behavior: this.motion.matches ? 'auto' : 'smooth' });
    }
  });
})();
